import { useState, useEffect } from "react";
import { toast } from "sonner";
import { pu } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { BellRing, Plus, Trash2, Save } from "lucide-react";

const DEFAULT_BTN = [
  { id: "cameriere", label: "Chiama il cameriere" },
  { id: "conto", label: "Il conto, per favore" },
  { id: "extra", label: "Acqua o pane" },
];

const MODI = [
  ["tutti", "A tutta la sala", "La chiamata arriva a chiunque sia in servizio: la prende chi è più vicino."],
  ["zona", "Al cameriere della zona", "Ogni zona ha il suo referente: la chiamata va a lui, gli altri non vengono disturbati."],
  ["cameriere", "Al cameriere del tavolo", "Va a chi ha aperto quel tavolo. Se il tavolo è libero, arriva a tutta la sala."],
  ["persona", "A una persona precisa", "Sempre alla stessa persona: il direttore di sala, oppure il titolare se quella figura non c'è."],
];

/** Chiamate dal tavolo: attivazione, a chi arrivano e quali richieste vede il cliente. */
export const ChiamateCard = () => {
  const { settings, zones, staff, refresh } = useApp();
  const [on, setOn] = useState(true);
  const [modo, setModo] = useState("tutti");
  const [btn, setBtn] = useState(DEFAULT_BTN);
  const [zmap, setZmap] = useState({});
  const [persona, setPersona] = useState("");
  const [cool, setCool] = useState(180);
  const [maxc, setMaxc] = useState(6);
  const [esc, setEsc] = useState(4);

  useEffect(() => {
    setOn(settings?.calls_enabled !== false);
    setModo(settings?.calls_routing || "tutti");
    setBtn(settings?.calls_buttons?.length ? settings.calls_buttons : DEFAULT_BTN);
    const m = {};
    (settings?.calls_zones || []).forEach((r) => { m[r.zone] = r.staff; });
    setZmap(m);
    setPersona(settings?.calls_person || "");
    setCool(settings?.calls_cooldown ?? 180);
    setMaxc(settings?.calls_max ?? 6);
    setEsc(settings?.calls_escalate ?? 4);
  }, [settings]);

  const salva = async () => {
    const rich = btn.filter((b) => (b.label || "").trim())
      .map((b, i) => ({ id: b.id || `r${i}`, label: b.label.trim().slice(0, 40) }));
    if (!rich.length) return toast.error("Serve almeno una richiesta");
    await pu("/settings", {
      ...settings,
      cover_charge: Number(settings.cover_charge) || 0,
      calls_enabled: on,
      calls_routing: modo,
      calls_person: persona,
      calls_cooldown: Math.max(0, Number(cool) || 0),
      calls_max: Math.max(1, Number(maxc) || 6),
      calls_escalate: Math.max(1, Number(esc) || 4),
      calls_buttons: rich,
      calls_zones: Object.entries(zmap).filter(([, v]) => v).map(([zone, s]) => ({ zone, staff: s })),
    });
    await refresh();
    toast.success("Chiamate dal tavolo aggiornate");
  };

  return (
    <Card className="space-y-4 p-6" data-testid="chiamate-card">
      <div className="flex items-start gap-3">
        <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <h3 className="font-semibold">Chiamate dal tavolo</h3>
          <p className="mt-1 text-sm text-slate-500">
            Il cliente inquadra il QR, guarda il menu e con un tocco può chiedere aiuto. La richiesta
            arriva subito in Sala con il numero del tavolo, con campanella e voce.
          </p>
        </div>
      </div>

      <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
        <span className="text-sm">Il cliente può chiamare dal menu digitale</span>
        <Switch checked={on} onCheckedChange={setOn} data-testid="chiamate-switch" />
      </label>

      <div className={on ? "space-y-3" : "hidden"}>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">A chi arriva la chiamata</p>
        {MODI.map(([id, titolo, nota]) => (
          <label
            key={id}
            data-testid={`chiamate-modo-${id}`}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${modo === id ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}
          >
            <input type="radio" name="chiamate-modo" checked={modo === id} onChange={() => setModo(id)} className="mt-0.5 h-4 w-4" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{titolo}</span>
              <span className="block text-xs text-slate-500">{nota}</span>
            </span>
          </label>
        ))}

        {modo === "persona" && (
          <div className="rounded-lg border border-slate-200 p-3" data-testid="chiamate-persona">
            <p className="text-xs text-slate-500">Chi riceve tutte le chiamate</p>
            <select
              className="mt-1.5 h-10 w-full rounded-md border border-slate-200 px-2 text-sm"
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              data-testid="chiamate-persona-select"
            >
              <option value="">Tutta la sala</option>
              {staff.filter((s) => s.active !== false).map((s) => (
                <option key={s.id} value={s.name}>{s.name}{s.role ? ` · ${s.role}` : ""}</option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-slate-500">
              Se questa persona non è in servizio, la chiamata arriva comunque a tutta la sala.
            </p>
          </div>
        )}

        {modo === "zona" && (
          <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="chiamate-zone">
            <p className="text-xs text-slate-500">Chi segue ogni zona</p>
            {zones.map((z) => (
              <div key={z.id} className="flex items-center gap-2">
                <Label className="w-28 shrink-0 truncate text-sm">{z.name}</Label>
                <select
                  className="h-10 flex-1 rounded-md border border-slate-200 px-2 text-sm"
                  value={zmap[z.name] || ""}
                  onChange={(e) => setZmap({ ...zmap, [z.name]: e.target.value })}
                  data-testid={`chiamate-zona-${z.name.toLowerCase()}`}
                >
                  <option value="">Tutta la sala</option>
                  {staff.filter((s) => s.active !== false).map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Per non farsi chiamare a raffica</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs">Attesa fra due chiamate</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input type="number" min="0" max="900" value={cool}
                       onChange={(e) => setCool(e.target.value)} data-testid="chiamate-cooldown" />
                <span className="text-xs text-slate-500">sec.</span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Chiamate per tavolo a serata</Label>
              <Input className="mt-1.5" type="number" min="1" max="30" value={maxc}
                     onChange={(e) => setMaxc(e.target.value)} data-testid="chiamate-max" />
            </div>
            <div>
              <Label className="text-xs">Se nessuno risponde entro</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input type="number" min="1" max="30" value={esc}
                       onChange={(e) => setEsc(e.target.value)} data-testid="chiamate-escalate" />
                <span className="text-xs text-slate-500">min.</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Un tavolo ha sempre <b>una sola chiamata aperta</b>: se il cliente ritocca il pulsante legge
            «stiamo arrivando» e a te non arriva un secondo avviso. Dopo che hai toccato «Fatto» il tavolo
            deve aspettare i secondi indicati, e oltre il tetto della serata gli viene chiesto gentilmente
            di fare un cenno al personale. Se la chiamata resta senza risposta per i minuti indicati passa
            a tutta la sala, così non resta appesa a chi in quel momento non può.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Cosa può chiedere il cliente</p>
          {btn.map((b, i) => (
            <div key={b.id || `btn-${i}`} className="flex items-center gap-2">
              <Input
                value={b.label || ""}
                maxLength={40}
                data-testid={`chiamate-btn-${i}`}
                onChange={(e) => setBtn(btn.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <Button variant="ghost" size="icon" className="text-rose-600" data-testid={`chiamate-btn-del-${i}`}
                      onClick={() => setBtn(btn.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {btn.length < 6 && (
            <Button variant="outline" size="sm" data-testid="chiamate-btn-add"
                    onClick={() => setBtn([...btn, { id: `r${Date.now()}`, label: "" }])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi richiesta
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-slate-50 p-4 text-xs text-slate-600" data-testid="chiamate-guida">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Come funziona, in breve</p>
        <ol className="mt-2 space-y-1.5">
          <li><b>1.</b> Stampa i QR da <b>Menu fisso → «QR da mettere sul tavolo»</b>. Scrivi il numero del tavolo prima di scaricare: così il cliente non deve scegliere nulla.</li>
          <li><b>2.</b> Il cliente inquadra, legge il menu e in fondo tocca il pulsante giallo <b>«Chiama il personale»</b>, poi la richiesta che gli serve.</li>
          <li><b>3.</b> A chi riceve arriva campanella e voce; in <b>Sala</b> compare la fascia «Chiamate dai tavoli» con tavolo, richiesta e minuti d'attesa.</li>
          <li><b>4.</b> Si tocca <b>«Ci vado io»</b> per prenderla in carico e <b>«Fatto»</b> quando è servita. Resta tutto nei Movimenti.</li>
        </ol>
      </div>

      <Button className="h-12 w-full" onClick={salva} data-testid="chiamate-save-btn">
        <Save className="mr-1.5 h-4 w-4" /> Salva le chiamate
      </Button>
    </Card>
  );
};
